import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaYoutube,
} from 'react-icons/fa';
import { ROUTES } from '@core/config/constants';
import type { FooterSocialLink } from '@core/tenant/useFooterStoreContent';
import { useFooterStoreContent } from '@core/tenant/useFooterStoreContent';
import './Footer.css';

function SocialIcon({ id }: { id: FooterSocialLink['id'] }) {
  switch (id) {
    case 'instagram':
      return <FaInstagram aria-hidden />;
    case 'whatsapp':
      return <FaWhatsapp aria-hidden />;
    case 'facebook':
      return <FaFacebookF aria-hidden />;
    case 'youtube':
      return <FaYoutube aria-hidden />;
    default:
      return null;
  }
}

export const Footer = () => {
  const f = useFooterStoreContent();
  const hasContact =
    f.contact.addressLines.length > 0 || f.contact.phone || f.contact.email;

  return (
    <footer className="footer">
      <div className="footer__container">
        <div className="footer__content">
          <div className="footer__section">
            <h3 className="footer__title">About Us</h3>
            <p className="footer__text">{f.aboutText}</p>
            {f.socialLinks.length > 0 ? (
              <div className="footer__social">
                {f.socialLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer__social-link"
                    aria-label={link.label}
                  >
                    <SocialIcon id={link.id} />
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          <div className="footer__section">
            <h3 className="footer__title">Quick Links</h3>
            <ul className="footer__list">
              <li>
                <Link to={ROUTES.HOME} className="footer__link">
                  Home
                </Link>
              </li>
              <li>
                <Link to={ROUTES.PRODUCTS} className="footer__link">
                  Shop
                </Link>
              </li>
              <li>
                <Link to={ROUTES.CATEGORIES} className="footer__link">
                  Categories
                </Link>
              </li>
              <li>
                <Link to={ROUTES.CART} className="footer__link">
                  Shopping Cart
                </Link>
              </li>
              <li>
                <Link to={ROUTES.ORDERS} className="footer__link">
                  My Orders
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer__section">
            <h3 className="footer__title">Customer Service</h3>
            <ul className="footer__list">
              <li>
                <Link to={ROUTES.CONTACT} className="footer__link">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to={ROUTES.FAQ} className="footer__link">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to={ROUTES.SHIPPING} className="footer__link">
                  Shipping Info
                </Link>
              </li>
              <li>
                <Link to={ROUTES.RETURNS} className="footer__link">
                  Returns
                </Link>
              </li>
              <li>
                <Link to={ROUTES.PRIVACY} className="footer__link">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer__section">
            <h3 className="footer__title">Contact Info</h3>
            {hasContact ? (
              <ul className="footer__list footer__list--contact">
                {f.contact.addressLines.length > 0 ? (
                  <li className="footer__contact-row footer__contact-row--address">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span className="footer__contact-address">
                      {f.contact.addressLines.map((line, i) => (
                        <Fragment key={`addr-${i}`}>
                          {i > 0 ? <br /> : null}
                          {line}
                        </Fragment>
                      ))}
                    </span>
                  </li>
                ) : null}
                {f.contact.phone ? (
                  <li>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <a
                      href={`tel:${f.contact.phone.replace(/[^\d+]/g, '')}`}
                      className="footer__contact-link"
                    >
                      {f.contact.phone}
                    </a>
                  </li>
                ) : null}
                {f.contact.email ? (
                  <li>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <a href={`mailto:${f.contact.email}`} className="footer__contact-link">
                      {f.contact.email}
                    </a>
                  </li>
                ) : null}
              </ul>
            ) : (
              <p className="footer__text">
                <Link to={ROUTES.CONTACT} className="footer__link">
                  Reach us via Contact
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className="footer__bottom">
          <p className="footer__copyright">
            &copy; {f.copyrightYear} {f.brandName}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
